import { ButtonV2 } from '@cimplico/components';
import { CloudArrowDownIcon } from '@heroicons/react/24/solid';
import { FYI_URL } from '../../config';
import { useViewLink } from '../../queries/use-view-link';
import { FileRecordNew, FileRecordTypeEnum } from '../../types/file-record';
import ImageViewer from './ImageViewer';
import PDFViewer from './PDFViewer';
import FYILogo from '../../assets/fyi.png';
import { DocumentIcon } from '@heroicons/react/20/solid';

type DocumentViewerProps = {
    record: FileRecordNew;
    onDownload?: () => void;
};
function DocumentRenderer({ record, onDownload }: DocumentViewerProps): JSX.Element | null {
    const { mimeType, metadata, recordType } = record.source ? record.source : record;

    const isImage = mimeType?.includes('image') || metadata?.documentType?.toLowerCase() === 'image';
    const isPDF = mimeType === 'application/pdf' || metadata?.documentType?.toLowerCase() === 'pdf';

    const isFYI = recordType === FileRecordTypeEnum.FYI_DOCUMENT;

    const canView = isImage || isPDF;

    // Get the view
    const viewQuery = useViewLink(record?.id as string, canView);

    const url = viewQuery.data?.url ?? viewQuery.data?.file;

    if (!canView) {
        return (
            <div className="flex flex-col items-center justify-center h-full w-full flex-1">
                <div className="flex flex-col items-center justify-center">
                    <DocumentIcon className="h-24 w-24 text-gray-600 opacity-20" />
                    <p className="text-gray-600">
                        Unable to preview <b>{record.name}</b>
                    </p>
                    <p className="text-xs text-gray-400  mb-4">*Previewing is available for pdf and image files</p>
                    <div className="flex gap-2 items-center">
                        <ButtonV2 label="Download" type="secondary" leftIcon={<CloudArrowDownIcon />} onClick={onDownload} />
                        {isFYI && (
                            <>
                                <label className="text-sm text-gray-700 font-medium">or</label>
                                <a
                                    className="text-sm text-gray-600 flex items-center hover:underline underline-offset-4"
                                    href={`${FYI_URL}/documents/${metadata?.id}/preview`}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <img src={FYILogo} className="h-6 w-6 mr-1" />
                                    View in FYI
                                </a>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Content */}
            {viewQuery.isLoading ? (
                <div className="h-full w-full flex grow bg-gray-300 animate-pulse"></div>
            ) : (
                <div className="bg-gray-300 flex-1 h-full w-full flex flex-col items-center relative overflow-auto">
                    {isPDF && url && <PDFViewer pdf={url} name={record.name} />}
                    {isImage && url && <ImageViewer src={url} alt={record.name} />}
                </div>
            )}
        </>
    );
}

export default DocumentRenderer;
